import { useEffect } from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import {
  applyTheme,
  resolveTheme,
  useThemeStore,
  type ThemePreference,
} from "../stores/theme";
import { cn } from "../lib/utils";

const ORDER: ThemePreference[] = ["system", "light", "dark"];

function labelFor(p: ThemePreference): string {
  return p;
}

function IconFor({ p }: { p: ThemePreference }) {
  if (p === "light") return <Sun className="w-3.5 h-3.5" />;
  if (p === "dark") return <Moon className="w-3.5 h-3.5" />;
  return <Monitor className="w-3.5 h-3.5" />;
}

export function ThemeToggle() {
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);

  useEffect(() => {
    applyTheme(resolveTheme(preference));
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme(resolveTheme("system"));
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference]);

  function cycle() {
    const idx = ORDER.indexOf(preference);
    const next = ORDER[(idx + 1) % ORDER.length]!;
    setPreference(next);
  }

  return (
    <button
      type="button"
      onClick={cycle}
      title={`Theme: ${labelFor(preference)} (click to cycle)`}
      className={cn(
        "flex items-center gap-1.5 border border-border/60 rounded px-2 py-1",
        "text-muted hover:text-fg hover:border-accent/60 transition-colors"
      )}
    >
      <IconFor p={preference} />
      <span className="hidden sm:inline">{labelFor(preference)}</span>
    </button>
  );
}
